import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload, 
  Download, 
  AlertTriangle, 
  CheckCircle, 
  FileText,
  Eye,
  Trash2
} from 'lucide-react';
import { Button } from './Button';
import { LoadingSpinner } from './LoadingSpinner';
import { PropertyResponse, CreatePropertyRequest, apiClient } from '@/services/api';
import { useToastHelpers } from '@/contexts/ToastContext';
import { 
  processCSVImport, 
  ImportResult, 
  generateSampleCSV,
  ParsedProperty,
  ImportValidationError
} from '@/utils/import';

interface PropertyImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingProperties: PropertyResponse[];
  onImportComplete: (newProperties: PropertyResponse[]) => void;
}

export function PropertyImportModal({ 
  isOpen, 
  onClose, 
  existingProperties, 
  onImportComplete 
}: PropertyImportModalProps) {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing'>('upload');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedForImport, setSelectedForImport] = useState<string[]>([]);
  const [showDuplicateDetails, setShowDuplicateDetails] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useToastHelpers();

  const resetModal = () => {
    setStep('upload');
    setCsvFile(null);
    setImportResult(null);
    setSelectedForImport([]);
    setShowDuplicateDetails(false);
    setShowErrorDetails(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      showError('Invalid File Type', 'Please select a CSV file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      showError('File Too Large', 'Please select a CSV file smaller than 5MB.');
      return;
    }

    setCsvFile(file);

    try {
      const content = await file.text();
      const result = processCSVImport(content, existingProperties);
      
      setImportResult(result);
      setSelectedForImport(result.valid.map(p => `${p.rowIndex}`));
      setStep('preview');
    } catch (error) {
      console.error('Failed to process CSV:', error);
      showError('Import Failed', error instanceof Error ? error.message : 'Failed to process CSV file.');
    }
  };

  const handleDownloadSample = () => {
    const sampleCSV = generateSampleCSV();
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_property_import.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSuccess('Sample Downloaded', 'Sample CSV file has been downloaded.');
  };

  const handlePropertyToggle = (rowIndex: string) => {
    setSelectedForImport(prev => 
      prev.includes(rowIndex)
        ? prev.filter(id => id !== rowIndex)
        : [...prev, rowIndex]
    );
  };

  const handleSelectAll = () => {
    if (!importResult) return;
    
    if (selectedForImport.length === importResult.valid.length) {
      setSelectedForImport([]);
    } else {
      setSelectedForImport(importResult.valid.map(p => `${p.rowIndex}`));
    }
  };

  const handleImport = async () => {
    if (!importResult || selectedForImport.length === 0) return;

    setStep('importing');

    try {
      const propertiesToImport = importResult.valid.filter(p => 
        selectedForImport.includes(`${p.rowIndex}`)
      );

      const createdProperties: PropertyResponse[] = [];

      for (const property of propertiesToImport) {
        try {
          // Create property data with analysis_status
          const propertyData = {
            ...property.data,
            // Note: analysis_status should be handled by the backend, but we may need to add it later
          };
          
          const created = await apiClient.createUserProperty(propertyData);
          createdProperties.push(created);
        } catch (error) {
          console.error(`Failed to create property at row ${property.rowIndex}:`, error);
          showError(
            'Import Error', 
            `Failed to import property at row ${property.rowIndex}: ${property.data.property_address}`
          );
        }
      }

      if (createdProperties.length > 0) {
        onImportComplete(createdProperties);
        showSuccess(
          'Import Successful', 
          `Successfully imported ${createdProperties.length} ${createdProperties.length === 1 ? 'property' : 'properties'}.`
        );
        handleClose();
      } else {
        showError('Import Failed', 'No properties were successfully imported.');
        setStep('preview');
      }
    } catch (error) {
      console.error('Import failed:', error);
      showError('Import Failed', 'An error occurred during import. Please try again.');
      setStep('preview');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getAnalysisTypeLabel = (analysisType: string) => {
    switch (analysisType) {
      case 'buy-hold': return 'Buy & Hold';
      case 'flip': return 'Fix & Flip';
      case 'brrr': return 'BRRR';
      default: return 'Analysis';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Import Properties</h2>
            <p className="text-gray-600 mt-1">
              {step === 'upload' && 'Upload a CSV file to import properties to your portfolio'}
              {step === 'preview' && 'Review and select properties to import'}
              {step === 'importing' && 'Importing selected properties...'}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            className="p-2"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 'upload' && (
            <div className="space-y-6">
              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Choose CSV File
                  </h3>
                  <p className="text-gray-600">
                    Upload a CSV file containing property data in the correct format
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button onClick={() => fileInputRef.current?.click()}>
                  Select CSV File
                </Button>
              </div>

              {/* Sample Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900">Need a template?</h4>
                    <p className="text-blue-700 text-sm mt-1">
                      Download a sample CSV file with the correct format and example data.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleDownloadSample}
                      className="mt-2 text-blue-700 border-blue-300 hover:bg-blue-100"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Sample CSV
                    </Button>
                  </div>
                </div>
              </div>

              {/* Format Requirements */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">CSV Format Requirements</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">Required Fields:</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Property Address</li>
                      <li>• Purchase Price</li>
                      <li>• Down Payment</li>
                      <li>• Loan Amount</li>
                      <li>• Interest Rate (%)</li>
                      <li>• Loan Term (Years)</li>
                      <li>• Annual Taxes</li>
                      <li>• Annual Insurance</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">Optional Fields:</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• City, State, ZIP Code</li>
                      <li>• Property Type</li>
                      <li>• Monthly HOA</li>
                      <li>• Expected Monthly Rent</li>
                      <li>• Total Renovation Costs</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'preview' && importResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Import Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {importResult.summary.totalRows}
                    </div>
                    <div className="text-sm text-gray-600">Total Rows</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {importResult.summary.validRows}
                    </div>
                    <div className="text-sm text-gray-600">Valid</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {importResult.summary.invalidRows}
                    </div>
                    <div className="text-sm text-gray-600">Invalid</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {importResult.summary.duplicateRows}
                    </div>
                    <div className="text-sm text-gray-600">Duplicates</div>
                  </div>
                </div>
              </div>

              {/* Errors */}
              {importResult.invalid.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h4 className="font-medium text-red-900">
                        Validation Errors ({importResult.invalid.length})
                      </h4>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {showErrorDetails ? 'Hide' : 'Show'} Details
                    </Button>
                  </div>
                  {showErrorDetails && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {importResult.invalid.map((error, index) => (
                        <div key={index} className="text-sm bg-white border border-red-200 rounded p-2">
                          <div className="font-medium text-red-900">Row {error.row}: {error.field}</div>
                          <div className="text-red-700">{error.error}</div>
                          {error.value && (
                            <div className="text-gray-600">Value: "{error.value}"</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Duplicates */}
              {importResult.duplicates.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      <h4 className="font-medium text-yellow-900">
                        Duplicate Properties ({importResult.duplicates.length})
                      </h4>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowDuplicateDetails(!showDuplicateDetails)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {showDuplicateDetails ? 'Hide' : 'Show'} Details
                    </Button>
                  </div>
                  <p className="text-sm text-yellow-800 mb-2">
                    These properties appear to already exist in your portfolio (same address and purchase price).
                  </p>
                  {showDuplicateDetails && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {importResult.duplicates.map((duplicate, index) => (
                        <div key={index} className="text-sm bg-white border border-yellow-200 rounded p-2">
                          <div className="font-medium text-yellow-900">
                            Row {duplicate.rowIndex}: {duplicate.data.property_address}
                          </div>
                          <div className="text-yellow-800">
                            Purchase Price: {formatCurrency(duplicate.data.purchase_price)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Valid Properties */}
              {importResult.valid.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-medium text-gray-900">
                        Properties Ready to Import ({selectedForImport.length} of {importResult.valid.length} selected)
                      </h4>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {selectedForImport.length === importResult.valid.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {importResult.valid.map((property, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 border-b border-gray-100 last:border-b-0">
                        <input
                          type="checkbox"
                          checked={selectedForImport.includes(`${property.rowIndex}`)}
                          onChange={() => handlePropertyToggle(`${property.rowIndex}`)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="font-medium text-gray-900 truncate">
                                {property.data.property_address}
                              </h5>
                              <div className="text-sm text-gray-600">
                                {property.data.city && `${property.data.city}, `}
                                {property.data.state} {property.data.zip_code}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium text-gray-900">
                                {formatCurrency(property.data.purchase_price)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {getAnalysisTypeLabel(property.analysisType || 'buy-hold')}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>Down: {formatCurrency(property.data.down_payment)}</span>
                            <span>Loan: {formatCurrency(property.data.loan_amount)}</span>
                            <span>Rate: {property.data.interest_rate}%</span>
                            <span>Term: {property.data.loan_term}yr</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <LoadingSpinner size="lg" />
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Importing Properties...
              </h3>
              <p className="text-gray-600 mt-2">
                Creating {selectedForImport.length} properties in your portfolio.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex items-center space-x-4">
            {step === 'preview' && csvFile && (
              <div className="text-sm text-gray-600">
                File: {csvFile.name} ({Math.round(csvFile.size / 1024)}KB)
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            {step === 'upload' && (
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
            )}
            
            {step === 'preview' && (
              <>
                <Button variant="secondary" onClick={() => setStep('upload')}>
                  Back
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={selectedForImport.length === 0}
                  className="flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Import {selectedForImport.length} {selectedForImport.length === 1 ? 'Property' : 'Properties'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}